import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Logout.css';

const Logout = () => {
    const [isComplete, setIsComplete] = useState(false);
    const { logout } = useAuth();

    useEffect(() => {
        const performLogout = async () => {
            try {
                // Clear Auth Context state
                logout();

                // Clear ALL localStorage
                localStorage.clear();

                // Clear ALL sessionStorage
                sessionStorage.clear();

                // Clear all cookies for this domain
                clearAllCookies();

                // Clear browser cache (Cache Storage API)
                await clearBrowserCache();

                // Unregister all service workers
                await unregisterServiceWorkers();

                // Clear IndexedDB databases
                await clearIndexedDB();

                setIsComplete(true);

                // Redirect to login after a brief delay
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1000);

            } catch (error) {
                console.error('Error during logout:', error);
                setIsComplete(true);

                // Still redirect even if some cleanup failed
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1000);
            }
        };

        performLogout();
    }, [logout]);

    // Clear all cookies for the current domain
    const clearAllCookies = () => {
        const cookies = document.cookie.split(';');

        for (let cookie of cookies) {
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

            if (name) {
                // Clear cookie with various path combinations
                const paths = ['/', '', window.location.pathname];
                const domains = [
                    '',
                    window.location.hostname,
                    '.' + window.location.hostname
                ];

                for (const path of paths) {
                    for (const domain of domains) {
                        // Clear without domain
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path || '/'}`;

                        // Clear with domain
                        if (domain) {
                            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path || '/'}; domain=${domain}`;
                        }

                        // Clear with Secure and SameSite flags
                        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path || '/'}; Secure; SameSite=Lax`;
                    }
                }
            }
        }
    };

    // Clear browser cache using Cache Storage API
    const clearBrowserCache = async () => {
        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();
                await Promise.all(
                    cacheNames.map(cacheName => caches.delete(cacheName))
                );
                console.log('Cache Storage cleared:', cacheNames);
            } catch (error) {
                console.warn('Could not clear Cache Storage:', error);
            }
        }
    };

    // Unregister all service workers
    const unregisterServiceWorkers = async () => {
        if ('serviceWorker' in navigator) {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(
                    registrations.map(registration => registration.unregister())
                );
                console.log('Service workers unregistered:', registrations.length);
            } catch (error) {
                console.warn('Could not unregister service workers:', error);
            }
        }
    };

    // Clear IndexedDB databases
    const clearIndexedDB = async () => {
        if ('indexedDB' in window) {
            try {
                // Get all database names if the browser supports it
                if (indexedDB.databases) {
                    const databases = await indexedDB.databases();
                    await Promise.all(
                        databases.map(db => {
                            return new Promise((resolve, reject) => {
                                const deleteReq = indexedDB.deleteDatabase(db.name);
                                deleteReq.onsuccess = () => resolve();
                                deleteReq.onerror = () => reject(deleteReq.error);
                                deleteReq.onblocked = () => {
                                    console.warn(`IndexedDB ${db.name} is blocked`);
                                    resolve();
                                };
                            });
                        })
                    );
                    console.log('IndexedDB databases cleared:', databases.length);
                }
            } catch (error) {
                console.warn('Could not clear IndexedDB:', error);
            }
        }
    };

    return (
        <div className="logout-page">
            {/* Event Banner with Logo */}
            <div className="logout-banner">
                <img
                    src="/Funds4Charity.png"
                    alt="Funds4Charity Logo"
                    className="logout-logo"
                />
            </div>

            <div className="logout-container">
                <div className="logout-card">
                    <div className="logout-header">
                        {isComplete ? (
                            <>
                                <div className="logout-icon success">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <h1>Logout Complete</h1>
                                <p>Redirecting to login...</p>
                            </>
                        ) : (
                            <>
                                <div className="logout-icon loading">
                                    <i className="fas fa-spinner fa-spin"></i>
                                </div>
                                <h1>Logging out...</h1>
                                <p>Clearing session data</p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Logout;
